/**
 * Pressure Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de pressão.
 *
 * Fontes:
 * - SI Brochure, 9th Edition (2019)
 * - NIST SP 811
 * - 10th CGPM (1954) - definição da atmosfera padrão
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type PressureUnit =
  // SI
  | "megapascal"
  | "kilopascal"
  | "hectopascal"
  | "pascal"
  // Bar
  | "bar"
  | "millibar"
  // Atmosphere
  | "atmosphere"
  // Mercury
  | "torr"
  | "mmhg"
  | "inhg"
  // Imperial
  | "psi"
  | "ksi"
  // Water column
  | "cmh2o"
  | "inh2o";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de pressão.
 *
 * Base: pascal (Pa) = 1 N/m² = 1 kg/(m·s²)
 *
 * Relações importantes:
 * - 1 atm = 101325 Pa (exato, CGPM 1954)
 * - 1 bar = 100000 Pa (exato por definição)
 * - 1 Torr = 101325/760 Pa (exato)
 * - 1 psi = 6894.757293168 Pa (derivado de lb/in²)
 */
export const PRESSURE_UNITS: Record<PressureUnit, SimpleUnitConfig> = {
  // SI Units (todos exatos)
  megapascal: {
    factor: 1e6,
    symbol: "MPa",
    singular: "megapascal",
    plural: "megapascals",
  },
  kilopascal: {
    factor: 1000,
    symbol: "kPa",
    singular: "kilopascal",
    plural: "kilopascals",
  },
  hectopascal: {
    factor: 100,
    symbol: "hPa",
    singular: "hectopascal",
    plural: "hectopascals",
  },
  pascal: {
    factor: 1,
    symbol: "Pa",
    singular: "pascal",
    plural: "pascals",
  },

  // Bar (exatos por definição)
  bar: {
    factor: 100000,
    symbol: "bar",
    singular: "bar",
    plural: "bar",
  },
  millibar: {
    factor: 100, // = 1 hPa
    symbol: "mbar",
    singular: "millibar",
    plural: "millibar",
  },

  // Atmosphere (exato, CGPM 1954)
  atmosphere: {
    factor: 101325,
    symbol: "atm",
    singular: "atmosphere",
    plural: "atmospheres",
  },

  // Mercury column
  torr: {
    factor: 101325 / 760, // ≈ 133.322368421
    symbol: "Torr",
    singular: "torr",
    plural: "torr",
  },
  mmhg: {
    factor: 133.322387415, // Convenção NIST
    symbol: "mmHg",
    singular: "millimeter of mercury",
    plural: "millimeters of mercury",
  },
  inhg: {
    factor: 3386.389, // Polegadas de mercúrio
    symbol: "inHg",
    singular: "inch of mercury",
    plural: "inches of mercury",
  },

  // Imperial (derivados de lb/in²)
  psi: {
    factor: 6894.757293168, // 1 lbf/in²
    symbol: "psi",
    singular: "pound per square inch",
    plural: "pounds per square inch",
  },
  ksi: {
    factor: 6894757.293168, // 1000 psi
    symbol: "ksi",
    singular: "kilopound per square inch",
    plural: "kilopounds per square inch",
  },

  // Water column
  cmh2o: {
    factor: 98.0665, // cm de água a 4°C
    symbol: "cmH₂O",
    singular: "centimeter of water",
    plural: "centimeters of water",
  },
  inh2o: {
    factor: 249.08891, // polegadas de água
    symbol: "inH₂O",
    singular: "inch of water",
    plural: "inches of water",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const PRESSURE_ALIASES: UnitAliases = {
  // megapascal
  mpa: "megapascal",
  // kilopascal
  kpa: "kilopascal",
  // hectopascal
  hpa: "hectopascal",
  // pascal
  pa: "pascal",
  // bar
  bar: "bar",
  // millibar
  mbar: "millibar",
  mb: "millibar",
  // atmosphere
  atm: "atmosphere",
  // torr
  torr: "torr",
  // mmhg
  mmhg: "mmhg",
  // inhg
  inhg: "inhg",
  // psi
  psi: "psi",
  // ksi
  ksi: "ksi",
  // cmh2o
  cmh2o: "cmh2o",
  // inh2o
  inh2o: "inh2o",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const PRESSURE_UNIT_LIST = Object.keys(PRESSURE_UNITS) as PressureUnit[];

/** Fatores de conversão (para compatibilidade) */
export const PRESSURE_FACTORS: Record<PressureUnit, number> =
  Object.fromEntries(
    Object.entries(PRESSURE_UNITS).map(([unit, config]) => [
      unit,
      config.factor ?? 1,
    ]),
  ) as Record<PressureUnit, number>;
